"use strict";

/**
 * Contains functions to map Product Status responses to the object fit for
 * Product (product.js) consumption in the addPricingFromStatus function.
 */
const ProductStatusMapper = {
  /**
   * Creates Product Status objects fit for Product (product.js) consumption from the V2 response
   * @param {Object} productStatusV2Response
   * @return {Array<object>}
   * @private
   */
  createProductStatusesFromV2: function(productStatusV2Response) {
    const productStatuses = [];
    if (productStatusV2Response && productStatusV2Response.items) {
      for (const productStatusSku of Object.keys(productStatusV2Response.items)) {
        const productStatusV2 = productStatusV2Response.items[productStatusSku];
        productStatusV2.sku = productStatusSku;
        const productStatus = this.createProductStatusFromV2(productStatusV2);
        productStatuses.push(productStatus);
      }
    }
    return productStatuses;
  },

  /**
   * Creates Product Status object fit for Product (product.js) consumption from its V2 counterpart
   * @param {Object} productStatusV2
   * @return {object}
   * @private
   */
  createProductStatusFromV2: function(productStatusV2) {
    const productStatus = {
      sku: productStatusV2.sku,
      globalProductID: productStatusV2.globalProductID,
      backordered: productStatusV2.backordered,
      backorderedAvailableDate: productStatusV2.backorderedAvailableDate,
      locallyProduced: productStatusV2.locallyProduced,
      availableQuantity: productStatusV2.availableQuantity,
      webEnabled: productStatusV2.webEnabled,
      status: getStatusPartFromV2(productStatusV2),
      orderType: productStatusV2.orderType,
      marketAttributes: productStatusV2.marketAttributes,
      childSkus: productStatusV2.childSkus != null ? productStatusV2.childSkus : []
    };
    addPricingFromV2(productStatusV2, productStatus);
    return productStatus
  }
};

/**
* Gets the status value (productStatus.status) from V2
* @param {Object} productStatusV2
* @return {String}
*/
function getStatusPartFromV2(productStatusV2) {
  const status = productStatusV2.status || {};
  const retailStatusIsReleased = status.retail === 'RELEASED_FOR_SALE';
  const wholesaleStatusIsReleased =
  status.wholesale === 'RELEASED_FOR_SALE';
  const retailNotSet = status.retail === 'NOT_SET_FOR_DIST_TYPE';
  const wholesaleNotSet = status.wholesale === 'NOT_SET_FOR_DIST_TYPE';
  if (retailStatusIsReleased || wholesaleStatusIsReleased) {
    return 'RELEASED_FOR_SALE';
  }

  if (retailNotSet || wholesaleNotSet) {
    if (retailNotSet && wholesaleNotSet) {
      return 'NOT_RELEASED_FOR_SALE';
    }
    if (retailNotSet) {
      return status.wholesale;
    }
    if (wholesaleNotSet) {
      return status.retail;
    }
  }

  return status.retail;
}

/**
* Adds Product Status Pricing from V2
* @param {Object} productStatusV2
* @param {Object} productStatus
*/
function addPricingFromV2(productStatusV2, productStatus) {
  if (productStatusV2 && productStatusV2.pricing) {
    const pricing = productStatusV2.pricing;
    Object.keys(pricing).forEach((channelKey) => {
      Object.keys(pricing[channelKey]).forEach((contextKey) => {
        Object.keys(pricing[channelKey][contextKey]).forEach((priceTypeKey) => {
          const priceValue = pricing[channelKey][contextKey][priceTypeKey];
          switch (channelKey) {
            case 'wholesale':
              switch (contextKey) {
                case 'webOrder':
                  addPrice(productStatus, priceTypeKey, 'WWHL', priceValue);
                  break;
                case 'webAdr':
                case 'WADW':
                case 'ADR5':
                  addPrice(productStatus, priceTypeKey, 'WADW', priceValue);
                  break;
                case 'order':
                case 'WHL':
                  addPrice(productStatus, priceTypeKey, 'WHL', priceValue);
                  break;
                case 'adr':
                  addPrice(productStatus, priceTypeKey, 'ADW', priceValue);
                  break;
                case 'ADR10':
                case 'WADWD':
                  addPrice(productStatus, priceTypeKey, 'WADWD', priceValue);
                  break;
                default:
                  addPrice(
                    productStatus,
                    priceTypeKey,
                    `${contextKey}-WWHL`,
                    priceValue
                  );
                  break;
              }
              break;
            case 'retail':
              switch (contextKey) {
                case 'webOrder':
                  addPrice(productStatus, priceTypeKey, 'WRTL', priceValue);
                  break;
                case 'webAdr':
                  addPrice(productStatus, priceTypeKey, 'WADR', priceValue);
                  break;
                case 'order':
                case 'RTL':
                  addPrice(productStatus, priceTypeKey, 'RTL', priceValue);
                  break;
                case 'adr':
                  addPrice(productStatus, priceTypeKey, 'ADR', priceValue);
                  break;
                default:
                  addPrice(
                    productStatus,
                    priceTypeKey,
                    `${contextKey}-WRTL`,
                    priceValue
                  );
                  break;
              }
              break;
          }
        });
      });
    });
  }
}

/**
* Adds a price tp the Product Status
* @param {Object} productStatus
* @param {String} priceTypeKey
* @param {String} subKey
* @param {number} priceValue
*/
function addPrice(productStatus, priceTypeKey, subKey, priceValue) {
  if (!['Points', 'cv', 'price', 'pv'].includes(priceTypeKey)) {
    // if the priceTypeKey is not one of these, then we don't want it to show.
    return;
  }
  if (priceTypeKey === 'pv') {
    priceTypeKey = 'psv';
  }
  if (priceTypeKey === 'cv') {
    priceTypeKey = 'csv';
  }

  if (productStatus[priceTypeKey] === undefined) {
    if (priceTypeKey === 'Points') {
      if (!productStatus.price) {
        productStatus.price = {};
      }
      productStatus.price.PTS = priceValue;
      return;
    } else {
      productStatus[priceTypeKey] = {};
    }
  }
  productStatus[priceTypeKey][subKey] = priceValue;
}

module.exports = ProductStatusMapper;
